// frontend/src/components/auth/ProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Box, CircularProgress } from '@mui/material';

/**
 * Map org membership role to equivalent system role for access checks.
 * Returns the higher of system role vs org role.
 */
function getEffectiveRole(systemRole, orgMembershipRole) {
  if (systemRole === 'super_admin') return 'super_admin';
  if (systemRole === 'admin') return 'admin';

  if (orgMembershipRole) {
    const orgRoleMap = {
      owner: 'admin',
      admin: 'admin',
      manager: 'project_manager',
      member: 'project_manager',
      reviewer: 'reviewer'
    };
    const mapped = orgRoleMap[orgMembershipRole];
    const hierarchy = ['reviewer', 'project_manager', 'admin', 'super_admin'];
    const sysLevel = hierarchy.indexOf(systemRole);
    const orgLevel = hierarchy.indexOf(mapped);
    return orgLevel > sysLevel ? mapped : systemRole;
  }

  return systemRole;
}

const ProtectedRoute = ({
  children,
  requiredRole = null,
  requiredPermission = null,
  requireAuth = true,
  redirectTo = '/login'
}) => {
  const { isAuthenticated, loading, user, hasPermission, canAccess } = useAuth();
  const { membership } = useOrganization();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if authentication is required
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access using effective role (system + org)
  if (requiredRole) {
    const effectiveRole = getEffectiveRole(user?.role, membership?.role);

    // Super Admin has access to everything
    if (effectiveRole === 'super_admin') {
      return children;
    }

    // Admin has access to everything except super_admin-only routes
    if (effectiveRole === 'admin' && requiredRole !== 'super_admin') {
      return children;
    }

    // Handle array of allowed roles
    if (Array.isArray(requiredRole)) {
      if (!requiredRole.includes(effectiveRole)) {
        if (effectiveRole === 'reviewer') {
          return <Navigate to="/projects" replace />;
        }
        return <Navigate to="/unauthorized" replace />;
      }
    } else {
      // Handle single role — check hierarchy (e.g., PM can access reviewer routes)
      const hierarchy = ['reviewer', 'project_manager', 'admin', 'super_admin'];
      const requiredLevel = hierarchy.indexOf(requiredRole);
      const effectiveLevel = hierarchy.indexOf(effectiveRole);

      if (effectiveLevel < requiredLevel) {
        if (effectiveRole === 'reviewer') {
          return <Navigate to="/projects" replace />;
        }
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  // Check permission-based access
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check resource-based access (using canAccess)
  if (requiredPermission && typeof requiredPermission === 'string') {
    const resourceMap = {
      '/dashboard': 'dashboard',
      '/analytics': 'analytics',
      '/users': 'users',
      '/settings': 'settings',
      '/batch': 'batch',
      '/projects/create': 'createProject'
    };

    const resource = resourceMap[location.pathname];
    if (resource && !canAccess(resource)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

// HOC for easier usage
export const withAuth = (Component, options = {}) => {
  return (props) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

// Route guards for specific roles
export const SuperAdminRoute = ({ children }) => (
  <ProtectedRoute requiredRole="super_admin">
    {children}
  </ProtectedRoute>
);

export const AdminRoute = ({ children }) => (
  <ProtectedRoute requiredRole="admin">
    {children}
  </ProtectedRoute>
);

export const ProjectManagerRoute = ({ children }) => (
  <ProtectedRoute requiredRole="project_manager">
    {children}
  </ProtectedRoute>
);

export const AdminOrProjectManagerRoute = ({ children }) => (
  <ProtectedRoute requiredRole={['admin', 'project_manager']}>
    {children}
  </ProtectedRoute>
);

export const ReviewerRoute = ({ children }) => (
  <ProtectedRoute requiredRole="reviewer">
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
